import React from 'react';
import { motion } from 'framer-motion';
import './style.scss';

export interface SectionCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  extra?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** Adds a subtle hover lift. */
  hoverable?: boolean;
  /** Removes inner body padding (e.g. for tables). */
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  style?: React.CSSProperties;
  delay?: number;
}

/**
 * Reusable rounded content card with an optional header. Presentation only.
 */
const SectionCard: React.FC<SectionCardProps> = ({
  title,
  subtitle,
  extra,
  icon,
  children,
  hoverable = false,
  flush = false,
  className,
  bodyClassName,
  style,
  delay = 0,
}) => {
  const hasHeader = Boolean(title || extra || icon);

  return (
    <motion.section
      className={`ui-section-card${hoverable ? ' ui-section-card--hoverable' : ''}${
        className ? ` ${className}` : ''
      }`}
      style={style}
      initial={{ opacity: 0, y: 22, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={hoverable ? { y: -5 } : undefined}
    >
      {hasHeader ? (
        <header className="ui-section-card__header">
          <div className="ui-section-card__heading">
            {icon ? <span className="ui-section-card__icon">{icon}</span> : null}
            <div>
              {title ? <h3 className="ui-section-card__title">{title}</h3> : null}
              {subtitle ? <p className="ui-section-card__subtitle">{subtitle}</p> : null}
            </div>
          </div>
          {extra ? <div className="ui-section-card__extra">{extra}</div> : null}
        </header>
      ) : null}
      <div
        className={`ui-section-card__body${flush ? ' ui-section-card__body--flush' : ''}${
          bodyClassName ? ` ${bodyClassName}` : ''
        }`}
      >
        {children}
      </div>
    </motion.section>
  );
};

export default SectionCard;
